import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuiz } from '../context/QuizContext';
import { formatCurrency } from '../utils/quizLogic';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

export function Result() {
  const { resultType, answers, leadData } = useQuiz();
  const navigate = useNavigate();

  useEffect(() => {
    if (!resultType) {
      navigate('/');
      return;
    }

    // Load TidyCal Script
    const script = document.createElement('script');
    script.src = "https://asset-tidycal.b-cdn.net/js/embed.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Clean up script if necessary, though TidyCal script is usually fine to stay
      const existingScript = document.querySelector('script[src="https://asset-tidycal.b-cdn.net/js/embed.js"]');
      if (existingScript) {
        document.body.removeChild(existingScript);
      }
    };
  }, [resultType, navigate]);

  if (!resultType) return null;

  const renderUninsured = () => (
    <div className="animate-fade-in">
      <div className="bg-brand-red/10 text-brand-red p-4 rounded-xl flex items-start mb-8">
        <SafeIcon icon={FiIcons.FiAlertTriangle} className="w-6 h-6 mr-3 flex-shrink-0 mt-1" />
        <div>
          <h2 className="text-xl font-bold">You're One Disaster Away From Losing Everything</h2>
          <p className="mt-1 text-sm text-red-900">Based on your answers, you currently have ZERO protection for your belongings or liability.</p>
        </div>
      </div>

      <div className="space-y-6 text-gray-700">
        <div>
          <h3 className="text-lg font-bold text-gray-900 flex items-center border-b pb-2">
            <span className="bg-red-100 text-red-600 w-6 h-6 rounded-full flex items-center justify-center text-xs mr-2">1</span>
            What You're Risking Right Now
          </h3>
          <ul className="mt-4 space-y-4">
            <li className="flex items-start">
              <SafeIcon icon={FiIcons.FiXCircle} className="text-red-500 mt-1 mr-2 flex-shrink-0" />
              <p><strong>Your Belongings:</strong> You estimated <strong>{formatCurrency(answers.q2)}</strong> in personal property. If there's a fire, flood, or break-in tonight, you lose it all. Your landlord's insurance covers the BUILDING - not your stuff.</p>
            </li>
            <li className="flex items-start">
              <SafeIcon icon={FiIcons.FiXCircle} className="text-red-500 mt-1 mr-2 flex-shrink-0" />
              <p><strong>Liability Risk:</strong> If someone slips in your apartment, YOU can be sued for medical bills (often $50K-$150K). They can garnish your wages.</p>
            </li>
            <li className="flex items-start">
              <SafeIcon icon={FiIcons.FiXCircle} className="text-red-500 mt-1 mr-2 flex-shrink-0" />
              <p><strong>Temporary Housing:</strong> If your apartment becomes unlivable, you're paying for a hotel out of pocket PLUS still paying rent ($2,000-$5,000).</p>
            </li>
          </ul>
        </div>

        <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
          <h4 className="font-bold text-gray-900 mb-3">The Shocking Truth:</h4>
          <ul className="space-y-2 text-sm">
            <li>• 95% of renters think their landlord's insurance covers them (it doesn't)</li>
            <li>• Average cost of a renter's insurance claim: $10,000-$30,000</li>
            <li>• Average cost of renter's insurance: <strong>$12-18/month</strong></li>
            <li>• That's less than Netflix, Spotify, or your daily coffee</li>
          </ul>
        </div>
      </div>
    </div>
  );

  const renderOverpaying = () => (
    <div className="animate-fade-in">
      <div className="bg-brand-navy/10 text-brand-navy p-4 rounded-xl flex items-start mb-8">
        <SafeIcon icon={FiIcons.FiSearch} className="w-6 h-6 mr-3 flex-shrink-0 mt-1" />
        <div>
          <h2 className="text-xl font-bold">You Might Be Overpaying (Or Underinsured)</h2>
          <p className="mt-1 text-sm text-blue-900">Good news: you have insurance! But let's make sure you're getting the best value.</p>
        </div>
      </div>

      <div className="space-y-6 text-gray-700">
        <div>
          <h3 className="text-lg font-bold text-gray-900 border-b pb-2">What We See</h3>
          <p className="mt-4">Based on your answers, there's a chance you're either:</p>
          <ul className="mt-4 space-y-4">
            <li className="flex items-start">
              <SafeIcon icon={FiIcons.FiDollarSign} className="text-brand-orange mt-1 mr-2 flex-shrink-0" />
              <p><strong>Overpaying</strong> - Average renter's insurance in CA is $15-18/month. Many people pay $25-35/month for basic coverage.</p>
            </li>
            <li className="flex items-start">
              <SafeIcon icon={FiIcons.FiAlertCircle} className="text-brand-orange mt-1 mr-2 flex-shrink-0" />
              <p><strong>Underinsured</strong> - Your belongings are worth more than you think, and your policy might have a $10K limit when you need $25K+.</p>
            </li>
            <li className="flex items-start">
              <SafeIcon icon={FiIcons.FiShieldOff} className="text-brand-orange mt-1 mr-2 flex-shrink-0" />
              <p><strong>Missing Coverage</strong> - Not all policies include liability, loss of use, or replacement cost.</p>
            </li>
          </ul>
        </div>

        <div className="bg-orange-50 p-6 rounded-xl border border-orange-100">
          <h4 className="font-bold text-orange-900 mb-3">The Hidden Costs:</h4>
          <p className="text-sm text-orange-800 mb-2">Many renters buy the first policy their landlord suggests. That can cost you:</p>
          <ul className="space-y-1 text-sm text-orange-800 list-disc list-inside">
            <li>$10-15/month extra ($120-180/year)</li>
            <li>Gaps in coverage when you file a claim</li>
            <li>"Actual cash value" instead of replacement cost</li>
          </ul>
        </div>
      </div>
    </div>
  );

  const renderEducation = () => (
    <div className="animate-fade-in">
      <div className="bg-brand-green/10 text-brand-green p-4 rounded-xl flex items-start mb-8">
        <SafeIcon icon={FiIcons.FiBookOpen} className="w-6 h-6 mr-3 flex-shrink-0 mt-1" />
        <div>
          <h2 className="text-xl font-bold">Let's Break Down Renter's Insurance</h2>
          <p className="mt-1 text-sm text-emerald-900">Most renters are confused about what it actually does. You're not alone.</p>
        </div>
      </div>

      <div className="space-y-6 text-gray-700">
        <div>
          <h3 className="text-lg font-bold text-gray-900 border-b pb-2">Here's What It REALLY Covers:</h3>
          
          <div className="mt-6 space-y-6">
            <div>
              <h4 className="font-bold flex items-center text-gray-900 mb-2">
                <SafeIcon icon={FiIcons.FiHome} className="mr-2 text-brand-green" /> 1. Your Stuff
              </h4>
              <p className="text-sm">If your apartment burns down, floods, or gets burglarized, it replaces furniture, electronics, clothes, etc. Your landlord's insurance ONLY covers the building structure.</p>
            </div>

            <div>
              <h4 className="font-bold flex items-center text-gray-900 mb-2">
                <SafeIcon icon={FiIcons.FiActivity} className="mr-2 text-brand-green" /> 2. Liability Protection
              </h4>
              <p className="text-sm">If someone gets hurt in your apartment (e.g., slips on stairs) or your bathtub overflows into the neighbor's unit, renter's insurance covers the lawsuit and medical bills.</p>
            </div>

            <div>
              <h4 className="font-bold flex items-center text-gray-900 mb-2">
                <SafeIcon icon={FiIcons.FiKey} className="mr-2 text-brand-green" /> 3. Temporary Housing
              </h4>
              <p className="text-sm">If your apartment is unlivable, it pays for a hotel/Airbnb while it's fixed, plus meals.</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
          <h4 className="font-bold text-gray-900 mb-3">💰 What It Actually Costs:</h4>
          <p className="text-sm mb-2">Most California renters pay <strong>$12-18/month</strong>.</p>
          <ul className="space-y-1 text-sm list-disc list-inside">
            <li>That's $0.40-0.60/day</li>
            <li>Less than a single Starbucks coffee</li>
            <li>Cheaper than Spotify or Netflix</li>
          </ul>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto grid md:grid-cols-5 gap-8">
        
        {/* Main Content Area */}
        <div className="md:col-span-3 bg-white p-8 rounded-2xl shadow-xl border border-gray-100">
          <p className="text-sm text-gray-500 mb-2">Prepared for {leadData.firstName}</p>
          
          {resultType === 'uninsured' && renderUninsured()}
          {resultType === 'overpaying' && renderOverpaying()}
          {resultType === 'education' && renderEducation()}

        </div>

        {/* Sidebar / CTA Area */}
        <div className="md:col-span-2 space-y-6">
          
          <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100 overflow-hidden">
            <h3 className="text-xl font-bold mb-6 text-gray-900">Schedule Your Discovery Call</h3>
            
            {/* TidyCal Embed */}
            <div 
              className="tidycal-embed" 
              data-path="pinoygeneralinsurance/discovery-call-phone-or-video"
            ></div>
            
            <p className="text-xs text-gray-400 mt-4 text-center">
              Simple, 5-minute call to answer all your questions.
            </p>
          </div>

          <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
            <h4 className="font-bold text-gray-900 mb-2 flex items-center">
              <SafeIcon icon={FiIcons.FiMail} className="mr-2 text-gray-400" />
              Check your inbox!
            </h4>
            <p className="text-sm text-gray-600">
              We just sent you:
              <br/>
              <strong className="text-brand-green mt-1 inline-block">
                {resultType === 'uninsured' && 'Free Belongings Value Calculator'}
                {resultType === 'overpaying' && "Renter's Insurance Discount Finder"}
                {resultType === 'education' && "Renter's Insurance 101 Guide"}
              </strong>
            </p>
          </div>

        </div>

      </div>
    </div>
  );
}